import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Calendar } from '@/components/ui/calendar';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { Separator } from '@/components/ui/Separator';
import { TimePickerPopover } from '@/components/ui/TimePickerPopover';
import { cn } from '@/lib/utils';

import { InlineField, PillField } from '../field';
import {
  fieldErrorId,
  labelIdOf,
  useElementId,
  useFieldChecks,
} from '../fieldIds';
import {
  dayLabel,
  isDateOnly,
  parseDateValue,
  presetDay,
  withDayOf,
  withTimeOf,
  writeDateValue,
  writeTimedValue,
  type DatePreset,
} from '../internals/dateValue';
import { formatClockTime, formatFullDateTime } from '../internals/localTime';
import { useGhostPills } from '../internals/objectContext';
import { useTimeFormat } from '../internals/useTimeFormat';
import {
  FIELD_ERROR_RING_CLASS,
  GHOST_PILL_CLASS,
  HELD_CLASS,
  ICON_CLASS,
  PILL_CLASS,
  PILL_ERROR_CLASS,
  PILL_PLACEHOLDER_CLASS,
  sizingClass,
  TEXT_BOX_CLASS,
  TONE_CLASS,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

const PRESETS: { preset: DatePreset; label: string }[] = [
  { preset: 'today', label: 'Today' },
  { preset: 'tomorrow', label: 'Tomorrow' },
  { preset: 'nextWeek', label: 'Next week' },
];

// `danger` is an overdue date; `done` a date on finished work
const DATE_TONE = {
  default: '',
  danger: TONE_CLASS.danger,
  done: TONE_CLASS.done,
} as const;

const PRESET_ROW_CLASS =
  'flex h-7 w-full items-center justify-between gap-6 rounded-md px-2 text-left text-xs text-foreground transition-colors duration-[120ms] hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none motion-reduce:duration-[60ms]';

const FIELD_TRIGGER_CLASS =
  'inline-flex w-auto items-center gap-[calc(6*var(--ac-u))] transition-colors duration-[120ms] motion-reduce:duration-[60ms]';

// A date reads as its day ("Tomorrow", "Thu, Sep 25"), plus its time when it has one
function valueLabel(
  value: string,
  withTime: boolean,
  prefer12Hr: boolean | null
): string | undefined {
  const date = parseDateValue(value);
  if (!date) return undefined;
  const day = dayLabel(date);
  return withTime && !isDateOnly(value)
    ? `${day} · ${formatClockTime(date, prefer12Hr)}`
    : day;
}

const Shown = ({
  text,
  previous,
  placeholder,
}: {
  text: string | undefined;
  previous: string | undefined;
  placeholder: string | undefined;
}) => (
  <>
    <Icons.calendar className={cn(ICON_CLASS, 'shrink-0')} aria-hidden="true" />
    {previous ? (
      <>
        <s className="whitespace-nowrap text-foreground-muted">{previous}</s>
        <span className="shrink-0 text-foreground-muted" aria-hidden="true">
          →
        </span>
      </>
    ) : null}
    {/* An empty span would still take the gap, pushing the lone icon off centre */}
    {text || placeholder ? (
      <span className={cn('truncate', !text && PILL_PLACEHOLDER_CLASS)}>
        {text ?? placeholder}
      </span>
    ) : null}
  </>
);

// A day, or a day and time, shown in the viewer's timezone. A picked value is
// written in the form the value came in, and a datetime with an offset takes the
// offset `timezone` has at the new instant
export const DateInput: ComponentFn<typeof catalog, 'DateInput'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const ghostPills = useGhostPills();
  const path = bindings?.value;
  const [value, setValue] = useBoundProp<string | null>(props.value, path);
  const checks = useFieldChecks({
    path,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const openedOnPress = useRef(false);
  const prefer12Hr = useTimeFormat();

  const held = status === 'running';
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const error = checks.error ?? props.error;
  const current = value ?? '';
  const withTime = props.withTime ?? false;
  const displayValue = props.dateOnly ? current.slice(0, 10) : current;
  const date = parseDateValue(displayValue);
  const hasTime = date !== null && !props.dateOnly && !isDateOnly(current);
  // Only a non-date value shows the text, so a pick or clear hides it before the saved row drops it
  const words = date === null && current !== '' ? props.text : undefined;
  // A pick over a value that is not a date is written as if the field were empty
  const like = date === null ? '' : current;
  const text = valueLabel(displayValue, withTime, prefer12Hr) ?? words;
  const previous = props.previous
    ? (valueLabel(
        props.dateOnly ? props.previous.slice(0, 10) : props.previous,
        withTime,
        prefer12Hr
      ) ?? props.previous)
    : undefined;
  const fullTime = hasTime
    ? formatFullDateTime(current, prefer12Hr)
    : date
      ? format(date, 'EEE, MMM d')
      : undefined;
  const detail = fullTime ?? words;
  const appearance = props.appearance ?? 'pill';
  // A pill hides its label, so its tooltip names the field above the full time
  const pillTooltip =
    props.label && detail
      ? `${props.label}\n${detail}`
      : (props.label ?? detail);
  const tooltip = error ?? (appearance === 'pill' ? pillTooltip : fullTime);
  const tone = DATE_TONE[props.tone ?? 'default'];

  // An optional empty pill is not drawn; focusing its path from the add line opens
  // it in place, and it leaves again if the picker closes with nothing picked
  const required =
    props.checks?.some((check) => check.type === 'required') ?? false;
  const hidesWhenEmpty =
    props.hideWhenEmpty === true && !required && current === '';
  const ghost =
    hidesWhenEmpty && ghostPills && editable && appearance === 'pill';
  const summoned =
    hidesWhenEmpty && path !== undefined && session.focused === path;
  useEffect(() => {
    if (summoned && editable) setOpen(true);
  }, [summoned, editable]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setPicking(false);
    if (!path) return;
    if (next) {
      controls.focus(path);
    } else {
      controls.blur(path);
      checks.onBlur();
    }
  };

  const write = (next: string | null, close: boolean) => {
    setValue(next);
    checks.afterChange();
    controls.commitNow();
    if (close) changeOpen(false);
  };

  // A new day keeps the time of day; with a time field open the popover stays
  const pickDay = (day: Date) =>
    write(
      props.dateOnly
        ? `${format(day, 'yyyy-MM-dd')}${like.slice(10)}`
        : writeDateValue(
            date && hasTime ? withDayOf(date, day) : day,
            like,
            props.timezone
          ),
      !withTime
    );

  const pickTime = (time: string) =>
    write(
      writeTimedValue(
        withTimeOf(date ?? startOfDay(new Date()), time),
        like,
        props.timezone
      ),
      true
    );

  const shown = (
    <Shown
      text={text}
      previous={previous}
      placeholder={
        ghost ? (props.label ?? props.placeholder) : props.placeholder
      }
    />
  );
  const describedBy = error ? fieldErrorId(id) : undefined;
  const pillClass = cn(
    PILL_CLASS,
    tone,
    ghost && GHOST_PILL_CLASS,
    error && PILL_ERROR_CLASS
  );
  const fieldClass = cn(
    TEXT_BOX_CLASS,
    FIELD_TRIGGER_CLASS,
    tone,
    error && FIELD_ERROR_RING_CLASS
  );

  if (hidesWhenEmpty && !ghost && !open && !summoned) return null;

  let control: ReactNode;
  if (!editable) {
    control = (
      <span
        id={id}
        title={tooltip}
        aria-labelledby={
          appearance === 'field' && props.label ? labelIdOf(id) : undefined
        }
        className={cn(
          appearance === 'pill'
            ? cn(pillClass, 'hover:bg-transparent')
            : cn(fieldClass, 'min-h-[var(--ac-field-lh)]'),
          held && HELD_CLASS
        )}
      >
        {shown}
      </span>
    );
  } else {
    control = (
      <Popover.Root open={open} onOpenChange={changeOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            id={id}
            title={tooltip}
            aria-label={
              appearance === 'pill' && props.label
                ? ghost
                  ? props.label
                  : `${props.label}: ${text ?? props.placeholder ?? ''}`
                : undefined
            }
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={
              appearance === 'pill'
                ? pillClass
                : cn(
                    fieldClass,
                    'h-[var(--ac-field-lh)] hover:bg-surface-hover'
                  )
            }
            // Opens on press, as the menus do: a field committing on blur can move this pill before the click arrives
            onPointerDown={(event) => {
              if (event.button !== 0 || event.ctrlKey || open) return;
              event.preventDefault();
              openedOnPress.current = true;
              changeOpen(true);
            }}
            onClick={(event) => {
              if (!openedOnPress.current) return;
              openedOnPress.current = false;
              event.preventDefault();
            }}
          >
            {shown}
          </button>
        </Popover.Trigger>
        <Popover.Content align="start" className="w-auto min-w-44 p-1">
          <YStack className="gap-px">
            {words ? (
              <>
                <span className="max-w-60 px-2 py-1 text-xs text-foreground-muted">
                  {words}
                </span>
                <Separator className="my-1 bg-border" />
              </>
            ) : null}
            {PRESETS.map(({ preset, label }) => {
              const day = presetDay(preset);
              return (
                <button
                  key={preset}
                  type="button"
                  className={PRESET_ROW_CLASS}
                  onClick={() => pickDay(day)}
                >
                  <span>{label}</span>
                  <span className="text-foreground-muted">
                    {format(day, 'EEE')}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              aria-expanded={picking}
              className={PRESET_ROW_CLASS}
              onClick={() => setPicking(!picking)}
            >
              Pick a date
            </button>
            {picking ? (
              <Calendar
                mode="single"
                selected={date ?? undefined}
                defaultMonth={date ?? undefined}
                onSelect={(day?: Date) => {
                  if (day) pickDay(day);
                }}
                autoFocus
                className="border-t border-border p-2"
              />
            ) : null}
            {withTime ? (
              <XStack className="mt-1 items-center justify-between gap-4 border-t border-border px-2 pt-1.5">
                <span className="text-xs text-foreground-muted">Time</span>
                <TimePickerPopover
                  selectedTime={date && hasTime ? format(date, 'HH:mm') : ''}
                  triggerAriaLabel="Time"
                  triggerClassName="h-6 w-auto px-2 text-xs"
                  onTimeSelected={pickTime}
                />
              </XStack>
            ) : null}
            {props.clearLabel && current !== '' ? (
              <>
                <Separator className="my-1 bg-border" />
                <button
                  type="button"
                  className={cn(PRESET_ROW_CLASS, 'text-foreground-subtle')}
                  onClick={() => {
                    write(null, true);
                    checks.clearErrors();
                  }}
                >
                  {props.clearLabel}
                </button>
              </>
            ) : null}
          </YStack>
        </Popover.Content>
      </Popover.Root>
    );
  }

  if (appearance === 'field') {
    return (
      <div className={sizingClass(props.sizing)}>
        <InlineField
          id={id}
          label={props.label}
          labelsControl={editable}
          error={error}
        >
          {control}
        </InlineField>
      </div>
    );
  }

  return (
    <PillField id={id} error={error} className={sizingClass(props.sizing)}>
      {control}
    </PillField>
  );
};
